import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Feature, CheckinFeature, CheckinProject, ManualOverrideType, Project, getProjects, ProjectStatus, getFeatures, FeatureStatus, getLocationsSystemFeature, getPeopleSystemFeature } from '../api/client'
import CombFill, { CombFillCountdown } from './CombFill'
import { EnhancedFeatureToken } from './checkin/EnhancedFeatureToken'
import type { LocationRow } from './checkin/LocationListEditor'
import type { MapRow } from './checkin/MapEditor'
import TombstoneDisclosure, { tombstoneKey } from './checkin/TombstoneDisclosure'
import ParseActivityPanel, { hasParseDetail } from './checkin/ParseActivityPanel'
import UnifiedSelector, { SelectedPerson, SelectedPlace } from './UnifiedSelector'
import type { ParseActivity } from '../hooks/useParseActivity'
import CheckinTimePicker from './CheckinTimePicker'
import { useTimestampDisplay } from '../hooks/useTimestampDisplay'
import type { AppliesPrecision } from '../utils/dates'
import { boolLabels } from '../utils/boolFeature'

// Scalar types whose value lives in `decimal_value`. Yes/no is one of them —
// 1 or 0, never a label in `freeform_value` (`docs/bool_features.md`).
const storesDecimal = (type: string | undefined) =>
    type === 'decimal' || type === 'rating' || type === 'labeled_rating' || type === 'bool'

// Common type for both new and existing checkins in the composer
export type ComposerCheckin = {
    body: string
    applies_to: string
    /** Time of day, `"HH:MM"` or `"HH:MM:SS"`, or null when the checkin has
     *  none — which is most of them. Carried through every transform and every
     *  save payload: an absent `applies_at` means *no time* to the server, not
     *  *leave it alone*, so a payload that drops it clears the time. */
    applies_at?: string | null
    /** How that time was set. Carried through the same transforms and the same
     *  save payloads as `applies_at`, and for a sharper version of the same
     *  reason: an omitted precision on a checkin that has a time reads as
     *  `exact`, so dropping it on an edit that never touched the time would
     *  quietly promote an `approximate` or `inferred` one. */
    applies_precision?: AppliesPrecision
    features: CheckinFeature[]
    projects: CheckinProject[]
    // The parse generation `features`/`projects` were taken from, carried so
    // the save can tell the server which parser output it is echoing. Absent
    // for a checkin that hasn't been fetched yet (the new-checkin placeholder).
    parse_generation?: number
}


interface CheckinComposerProps {
    editingCheckinId: number | null
    composerCheckin: ComposerCheckin
    features: Feature[]
    recentFeatures: Feature[]
    collectionItems: Record<string, any>
    onFieldEdited: (checkinId: number | null, field: 'body' | 'date' | 'time' | 'feature' | 'project', checkin: ComposerCheckin, featureId?: number, projectId?: number) => void
    onDelete?: (checkinId: number) => void
    onCancel?: () => void
    /** Forwarded down to the location picker so picker-created Places land
     *  in the page-level collection-item cache (avoids "Place #N" rendering
     *  after a save+close+reopen of the location list). */
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    projectDetails?: Project[] // Project details from the checkin for immediate display
    savingState?: 'unsaved' | 'saving' | 'ai-parsing' | null
    /** The auto-parse timer currently armed for this checkin, if any. Drives
     *  the countdown ring; absent when no parse is waiting. */
    parseCountdown?: { armedAt: number; dueAt: number } | null
    /** Skip the remaining wait and parse now. */
    onParseNow?: () => void
    /** The live record of this checkin's parse, when one is running. The same
     *  record the read-view row gets -- while a composer is open that row is
     *  not mounted, so this is the only place the panel can appear during the
     *  flow where a parse is actually watched. Absent (re-analyze, bulk parse,
     *  the window before the socket opens) leaves the static comb alone. */
    activity?: ParseActivity
    /** Expand / collapse the parse panel. Absent leaves it non-expandable. */
    onToggleParseExpanded?: (expanded: boolean) => void
}

// A save is usually a single fast PUT, so `savingState` can go to 'saving'
// and back inside 100ms. Rendered literally that is a comb that appears and
// vanishes, which reads as a glitch rather than as feedback. So: don't show it
// until it has lasted long enough to be worth reading, and once shown keep it
// long enough to be read. Parsing needs neither -- it runs for seconds.
const SAVING_SHOW_AFTER_MS = 400
const SAVING_MIN_VISIBLE_MS = 500

/** True once `active` has held for `showAfter`, and then for at least
 *  `minVisible` afterwards -- so a flag that flickers on and off never
 *  renders at all, and one that does render is legible. */
const useSettledFlag = (active: boolean, showAfter: number, minVisible: number) => {
    const [shown, setShown] = useState(false)
    const shownAt = useRef(0)

    useEffect(() => {
        if (active) {
            // Already up: stay up, and drop any pending hide.
            if (shown) return
            const t = setTimeout(() => {
                shownAt.current = Date.now()
                setShown(true)
            }, showAfter)
            return () => clearTimeout(t)
        }
        if (!shown) return
        const remaining = shownAt.current + minVisible - Date.now()
        if (remaining <= 0) {
            setShown(false)
            return
        }
        const t = setTimeout(() => setShown(false), remaining)
        return () => clearTimeout(t)
    }, [active, shown, showAfter, minVisible])

    return shown
}

/** What the composer is busy with: waiting to parse, saving, or parsing.
 *
 *  Module-level so it keeps its identity across the composer's renders --
 *  declared inline it was a fresh component type every render, so React tore
 *  the subtree down and rebuilt it on each keystroke and the comb restarted
 *  from an empty cell every time.
 *
 *  Saving and parsing share one element rather than having a branch each, so
 *  the comb survives the hand-off between them: same type, same position, so
 *  React keeps the DOM node and the CSS animation runs on undisturbed while
 *  only the word changes. They are two phases of one wait, and a comb that
 *  jumped back to empty halfway through would say otherwise. The countdown is
 *  a separate element and does restart -- it is a different thing, measuring a
 *  wait rather than reporting work. */
const SavingStateIndicator: React.FC<{
    state: 'unsaved' | 'saving' | 'ai-parsing' | null
    parseCountdown?: { armedAt: number; dueAt: number } | null
    onParseNow?: () => void
    activity?: ParseActivity
    onToggleParseExpanded?: (expanded: boolean) => void
}> = ({ state, parseCountdown, onParseNow, activity, onToggleParseExpanded }) => {
    const parsing = state === "ai-parsing"
    // A parse takes over the moment it starts, however much of the save's
    // minimum is left: it is the louder state, and the comb carries on
    // regardless since only the word changes.
    const saving = useSettledFlag(state === "saving", SAVING_SHOW_AFTER_MS, SAVING_MIN_VISIBLE_MS)
    const busy = parsing || saving
    // The live panel stands in for the static comb only when a parse is
    // running *and* a record for it exists. A multi-checkin bulk parse goes
    // over REST with no socket and so has no record, as does the window before
    // a parse's socket opens; both keep the word they have always had rather
    // than regressing to a blank line.
    //
    // This is the one place the save-to-parse hand-off does re-mount the comb
    // (a span cannot hold the panel's div), and deliberately: the live comb is
    // a phased one counting the parse's own steps, not a continuation of the
    // save's wait.
    const live = parsing && activity ? activity : null
    // Only an open drawer needs the row to grow. Collapsed, the panel is the
    // same single `min-h-6` line the pill was, so the row stays exactly the
    // row it has always been -- which is also what keeps the saving state and
    // the countdown button untouched.
    const drawerOpen = Boolean(live?.expanded && hasParseDetail(live))

    return (
        <div className={drawerOpen ? "flex items-start min-h-6 mt-2" : "flex items-center h-6 mt-2"}>
            {/* Only while nothing is already running -- a parse in flight
                is its own, louder, status. Gated on what is actually on
                screen, not on the raw state: a save too short to show is also
                too short to be worth blinking the countdown out for. */}
            {parseCountdown && !busy && (
                <motion.button
                    type="button"
                    onClick={onParseNow}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    style={{ transformOrigin: 'left center' }}
                    /* Shaped like the status pill it leads to, so hovering it
                       previews what tapping it produces. Bordered and in a
                       readable weight rather than flat grey text, which reads
                       as disabled on something that is the one thing here you
                       can actually press. */
                    className="group flex items-center gap-1.5 rounded-full border border-gray-200 bg-white py-0.5 pl-2 pr-3 text-sm font-medium text-gray-600 transition-colors hover:border-amber-200 hover:bg-amber-50 hover:text-amber-800"
                    title="Analyze now (⌘↵)"
                >
                    {/* The comb stays quieter than the label: it is counting
                        down, not asking to be read. */}
                    <span className="inline-flex text-gray-400 transition-colors group-hover:text-honey">
                        <CombFillCountdown armedAt={parseCountdown.armedAt} dueAt={parseCountdown.dueAt} />
                    </span>
                    Analyze now
                </motion.button>
            )}
            {/* The panel's own arrival and departure, in the same vocabulary
                the drawer behind its chevron uses: a parse starting used to
                snap the panel onto the row and a parse ending used to snap it
                off (reported 2026-09-18). `AnimatePresence` sits outside the
                condition rather than around the branch, so the panel is still
                mounted while it leaves — inside it, unmounting the presence
                and the child together would give the exit nowhere to run. */}
            <AnimatePresence>
                {busy && live && (
                    <motion.div
                        key="parse-activity-reveal"
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        style={{ transformOrigin: 'top left' }}
                        className="min-w-0"
                        data-testid="parse-activity-reveal"
                    >
                        <ParseActivityPanel
                            activity={live}
                            onToggleExpanded={onToggleParseExpanded}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
            {busy && !live && (
                <span
                    role="status"
                    className="text-honey flex items-center gap-2 rounded-full bg-amber-50 py-0.5 pl-2 pr-3 text-sm"
                >
                    <CombFill size={16} />
                    <span className="font-medium text-amber-800">
                        {parsing ? "Analyzing" : "Saving"}
                    </span>
                </span>
            )}
        </div>
    )
}

const CheckinComposer: React.FC<CheckinComposerProps> = ({
    editingCheckinId,
    composerCheckin,
    features,
    recentFeatures,
    collectionItems,
    onFieldEdited,
    onDelete,
    onCancel,
    onCollectionItemReady,
    projectDetails,
    savingState,
    parseCountdown,
    onParseNow,
    activity,
    onToggleParseExpanded
}) => {
    // Tab state
    const [activeTab, setActiveTab] = useState<'simple' | 'explicit'>('simple')

    // The composer is where a user first expresses a time, and how they
    // expressed it is what the timeline's display mode follows the first time
    // round -- see `handleTimeChange` below.
    const { noteCheckinTimeSet } = useTimestampDisplay()

    // Locally-cached system features the composer has bootstrapped (currently
    // just the user-global Locations feature). Held here — instead of pushed
    // back up to the page — so adding a place from the inline selector renders
    // immediately without requiring a refreshFeatures round-trip. The page's
    // own fetchData picks up the feature on the next reload.
    const [bootstrappedFeatures, setBootstrappedFeatures] = useState<Feature[]>([])

    // Tracks the feature whose list-editor popover is currently open. The
    // chip rendering uses this to keep the chip mounted (via a placeholder
    // CheckinFeature row) when the user empties the list mid-edit; without
    // it, deleting the last row would unmount the chip and tear down the
    // popover the user is still interacting with. Cleared on dismiss.
    const [editingFeatureId, setEditingFeatureId] = useState<number | null>(null)

    // The tombstone disclosure defaults to collapsed every time the composer
    // opens for a different checkin, by design (no per-session memory — keeps
    // the surface quiet). Reset on `editingCheckinId` change.
    const [tombstonesExpanded, setTombstonesExpanded] = useState(false)
    useEffect(() => { setTombstonesExpanded(false) }, [editingCheckinId])

    // Autofocus the body textarea when the composer mounts as a brand-new
    // checkin. The new-checkin path passes a negative placeholder id (see
    // NEW_CHECKIN_ID_PLACEHOLDER in CheckinsPage); the edit path passes a
    // real positive id. setTimeout defers past the action-menu's
    // outside-click handler, which otherwise reclaims focus to <body>.
    const bodyTextareaRef = useRef<HTMLTextAreaElement>(null)
    useEffect(() => {
        if (editingCheckinId != null && editingCheckinId < 0) {
            const t = setTimeout(() => bodyTextareaRef.current?.focus(), 0)
            return () => clearTimeout(t)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Sticky auto-grow for the body textarea: only ever grows within an
    // editing session, so backspacing over a line doesn't visibly shrink
    // the composer.
    const growBodyToFit = useCallback(() => {
        const ta = bodyTextareaRef.current
        if (!ta) return
        if (ta.scrollHeight > ta.clientHeight) {
            ta.style.height = `${ta.scrollHeight}px`
        }
    }, [])
    // Drop the inline height when the composer switches checkins, then
    // re-measure. The id also flips placeholder→real when a new checkin
    // autosaves, and there the body is unchanged — without the re-measure
    // a large pasted body collapses back to the two-row default.
    useEffect(() => {
        if (bodyTextareaRef.current) bodyTextareaRef.current.style.height = ''
        growBodyToFit()
    }, [editingCheckinId, growBodyToFit])
    useEffect(() => {
        growBodyToFit()
    }, [composerCheckin.body, activeTab, growBodyToFit])

    // Single feature-definition lookup, shared by the chip renderer and by
    // the autocomplete's place-dedup filter.
    const allFeaturesById = useMemo(() => {
        const m = new Map<number, Feature>()
        for (const f of [...recentFeatures, ...features, ...bootstrappedFeatures]) {
            m.set(f.id, f)
        }
        return m
    }, [recentFeatures, features, bootstrappedFeatures])

    // composerCheckin.features augmented with a value-less placeholder row
    // for `editingFeatureId` when its real rows have all been deleted. Keeps
    // the chip (and the popover anchored inside it) mounted while the user
    // is still editing. The placeholder lives only in the render path; it
    // doesn't reach `composerCheckin.features` or the backend. Real rows for
    // the feature override the placeholder — once a new row is committed,
    // the placeholder vanishes on the next render.
    const featuresForDisplay = useMemo(() => {
        if (editingFeatureId == null) return composerCheckin.features
        const hasLiveRow = composerCheckin.features.some(
            (f) => f.feature_id === editingFeatureId && f.manual_override !== ManualOverrideType.DELETE
        )
        if (hasLiveRow) return composerCheckin.features
        return [...composerCheckin.features, { feature_id: editingFeatureId } as CheckinFeature]
    }, [composerCheckin.features, editingFeatureId])

    // Project selection state
    const [projects, setProjects] = useState<Project[]>([])

    // Explicit tab state
    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null)
    const [projectFeatures, setProjectFeatures] = useState<Feature[]>([])
    const [explicitFeatureValues, setExplicitFeatureValues] = useState<Map<number, CheckinFeature>>(new Map())

    // Project lookup for the tombstone disclosure's project-prefixed labels.
    // Same source pair the chip layout uses (projectDetails preferred,
    // projects as fallback) — `feature.project` isn't reliably hydrated on
    // every Feature object that flows through the composer.
    const projectsById = useMemo(() => {
        const m = new Map<number, { id: number; title: string; emoji?: string }>()
        for (const p of projects) m.set(p.id, p)
        if (projectDetails) for (const p of projectDetails) m.set(p.id, p)
        return m
    }, [projects, projectDetails])

    // Load active projects when component mounts
    useEffect(() => {
        const loadProjects = async () => {
            try {
                const projectList = await getProjects(ProjectStatus.ACTIVE)
                setProjects(projectList)

                // Preselect project for explicit tab
                if (!selectedProjectId) {
                    // If editing and checkin has projects, select the first one
                    const existingProjectIds = composerCheckin.projects
                        .filter(p => p.manual_override !== ManualOverrideType.DELETE)
                        .map(p => p.project_id)

                    if (existingProjectIds.length > 0) {
                        // Select first existing project from checkin
                        const firstExistingProject = projectList.find(p => existingProjectIds.includes(p.id))
                        if (firstExistingProject) {
                            setSelectedProjectId(firstExistingProject.id)
                        }
                    } else {
                        // For new checkins, try to use the last selected project from localStorage
                        const savedProjectId = (editingCheckinId === null || editingCheckinId === -1) ? localStorage.getItem('lastSelectedProjectId') : null
                        const savedId = savedProjectId ? parseInt(savedProjectId) : null

                        if (savedId && projectList.some(p => p.id === savedId)) {
                            setSelectedProjectId(savedId)
                        } else if (projectList.length > 0) {
                            // Otherwise select first available project
                            setSelectedProjectId(projectList[0].id)
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading projects:', err)
            }
        }

        loadProjects()
    }, [])

    // Load features for selected project in explicit tab
    useEffect(() => {
        if (selectedProjectId && activeTab === 'explicit') {
            const loadProjectFeatures = async () => {
                try {
                    // Get all features and filter by project
                    const allFeatures = await getFeatures(FeatureStatus.ACTIVE)
                    const projectSpecificFeatures = allFeatures.filter(f => f.project_id === selectedProjectId)
                    setProjectFeatures(projectSpecificFeatures)

                    // Initialize feature values - either from existing checkin or default active state
                    const valuesMap = new Map<number, CheckinFeature>()

                    projectSpecificFeatures.forEach(feature => {
                        // Check if feature already has a value in the checkin
                        const existingFeature = composerCheckin.features.find(cf => cf.feature_id === feature.id)

                        if (existingFeature) {
                            // Convert existing value to freeform_value for display in explicit mode
                            const featureType = feature.config?.type || 'freeform'
                            let displayValue = ''

                            if (storesDecimal(featureType)) {
                                displayValue = existingFeature.decimal_value?.toString() || ''
                            } else if (featureType === 'reference') {
                                displayValue = existingFeature.reference_value?.toString() || ''
                            } else {
                                displayValue = existingFeature.freeform_value || ''
                            }

                            valuesMap.set(feature.id, {
                                ...existingFeature,
                                freeform_value: displayValue // Store display value in freeform_value for the UI
                            })
                        }
                        // Don't add features that haven't been explicitly set
                    })

                    setExplicitFeatureValues(valuesMap)
                } catch (err) {
                    console.error('Error loading project features:', err)
                }
            }

            loadProjectFeatures()
        }
    }, [selectedProjectId, activeTab])

    // Save selected project ID to localStorage when it changes (only for explicit tab)
    useEffect(() => {
        if (selectedProjectId && activeTab === 'explicit') {
            // Only save if we're creating a new checkin (not editing)
            if (editingCheckinId === null || editingCheckinId === -1) {
                localStorage.setItem('lastSelectedProjectId', selectedProjectId.toString())
            }
        }
    }, [selectedProjectId, activeTab, editingCheckinId])

    // Handle text field changes with auto-save and AI parse
    const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const updatedCheckin = {
            ...composerCheckin,
            body: e.target.value
        }

        onFieldEdited(editingCheckinId, "body", updatedCheckin)
    }

    // Handle date field changes
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedCheckin = {
            ...composerCheckin,
            applies_to: e.target.value
        }

        onFieldEdited(editingCheckinId, "date", updatedCheckin)
    }

    // Handle time-of-day changes. `CheckinTimePicker` has already normalized
    // the value: `null` for a cleared control, `"HH:MM"` otherwise — never
    // `"00:00"` for empty, which would stamp a real midnight on a checkin
    // that has no time (`docs/checkin_time.md`).
    //
    // The precision comes from the picker rather than being decided here,
    // because only the picker knows which of its two routes the value took —
    // the clock input (`exact`) or a bucket word (`approximate`). By the time
    // the string arrives, they are indistinguishable.
    //
    // It is also what the display mode follows the first time anyone sets a
    // time: a typed clock means they want the clock, a tapped word means they
    // want the word. Reported rather than decided here — the server owns the
    // "at most once, and never over a choice" rule, and answers with the state
    // the cached user object should now carry. Silent either way, and a
    // failure never touches the edit below it.
    const handleTimeChange = (appliesAt: string | null, precision: AppliesPrecision) => {
        void noteCheckinTimeSet(precision)
        onFieldEdited(editingCheckinId, "time", {
            ...composerCheckin,
            applies_at: appliesAt,
            applies_precision: precision,
        })
    }

    // Step the date by ±N days. UTC math so DST never shifts the result.
    const stepDate = (offset: number) => {
        const parts = composerCheckin.applies_to.split('-').map(Number)
        if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) return
        const [y, m, d] = parts
        const date = new Date(Date.UTC(y, m - 1, d))
        date.setUTCDate(date.getUTCDate() + offset)
        const next = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
        onFieldEdited(editingCheckinId, "date", { ...composerCheckin, applies_to: next })
    }


    const handleFeatureToggle = (featureId: number) => {
        // Check if this feature is already in our list
        const featureIndex = composerCheckin.features.findIndex(f => f.feature_id === featureId)
        let updatedFeatures = [...composerCheckin.features]

        // List/map features are cleared wholesale, the same way emptying them
        // through the per-item editor does it: every row of the feature is
        // replaced by one sentinel (all value columns NULL, DELETE), which is
        // the feature-level lock model's marker for "user cleared this"
        // (`docs/list_tagging.md`). Per-row DELETE tombstones that keep their
        // values belong to the older model, and for a
        // reference list they are self-erasing: nothing treats a DELETE row as
        // a live reference, so the GC drops the collection item and the FK
        // cascade takes the tombstone with it. Scalars are untouched — their
        // tombstones deliberately keep their value columns so the last-known
        // value stays visible for review.
        const toggledFeature = recentFeatures.find(f => f.id === featureId) ||
                               features.find(f => f.id === featureId) ||
                               bootstrappedFeatures.find(f => f.id === featureId)
        const toggledType = toggledFeature?.config?.type
        if (featureIndex >= 0 && (toggledType === 'list' || toggledType === 'map')) {
            const updatedCheckin = {
                ...composerCheckin,
                features: [
                    ...composerCheckin.features.filter(f => f.feature_id !== featureId),
                    { feature_id: featureId, manual_override: ManualOverrideType.DELETE },
                ],
            }
            onFieldEdited(editingCheckinId, "feature", updatedCheckin, featureId)
            return
        }

        // Adding a yes/no feature is the one-tap Yes. No is always a
        // deliberate pick on the token, never a side effect of adding.
        const addedValue = toggledType === 'bool' ? { decimal_value: 1 } : {}

        if (featureIndex >= 0) {
            // Check if this feature has a manual override that's not DELETE
            const existingFeature = composerCheckin.features[featureIndex]
            if (existingFeature.manual_override === ManualOverrideType.DELETE) {
                // If it was previously marked as DELETE, update to KEEP_VALUE since user is checking it
                updatedFeatures = updatedFeatures.map(f =>
                    f.feature_id === featureId ?
                        { ...f, ...addedValue, manual_override: ManualOverrideType.KEEP_VALUE } :
                        f
                )
            } else {
                // For non-DELETE features, mark with DELETE when unchecking
                updatedFeatures = updatedFeatures.map(f =>
                    f.feature_id === featureId ?
                        { ...f, manual_override: ManualOverrideType.DELETE } :
                        f
                )
            }
        } else {
            // Add this feature if it doesn't exist
            // Set manual_override to KEEP_VALUE since user explicitly added it
            updatedFeatures.push({
                feature_id: featureId,
                ...addedValue,
                manual_override: ManualOverrideType.KEEP_VALUE
            })
        }

        const updatedCheckin = {
            ...composerCheckin,
            features: updatedFeatures
        }

        onFieldEdited(editingCheckinId, "feature", updatedCheckin, featureId)
    }

    // Handle adding/removing projects
    const handleProjectToggle = (projectId: number) => {
        // Check if this project is already in our list
        const projectIndex = composerCheckin.projects.findIndex(p => p.project_id === projectId)
        let updatedProjects = [...composerCheckin.projects]

        if (projectIndex >= 0) {
            // Check if this project has a manual override that's not DELETE
            const existingProject = composerCheckin.projects[projectIndex]
            if (existingProject.manual_override === ManualOverrideType.DELETE) {
                // If it was previously marked as DELETE, update to KEEP_VALUE since user is checking it
                updatedProjects = updatedProjects.map(p =>
                    p.project_id === projectId ?
                        { ...p, manual_override: ManualOverrideType.KEEP_VALUE } :
                        p
                )
            } else {
                // For non-DELETE projects, mark with DELETE when unchecking
                updatedProjects = updatedProjects.map(p =>
                    p.project_id === projectId ?
                        { ...p, manual_override: ManualOverrideType.DELETE } :
                        p
                )
            }
        } else {
            // Add this project if it doesn't exist
            // Set manual_override to KEEP_VALUE since user explicitly added it
            updatedProjects.push({
                project_id: projectId,
                manual_override: ManualOverrideType.KEEP_VALUE
            })
        }

        const updatedCheckin = {
            ...composerCheckin,
            projects: updatedProjects
        }

        onFieldEdited(editingCheckinId, "project", updatedCheckin, /* featureId */ undefined, projectId)
    }

    // Add a feature from the dropdown (can be Feature object or just ID)
    const handleAddFeature = (feature: Feature | number) => {
        const featureId = typeof feature === 'number' ? feature : feature.id
        handleFeatureToggle(featureId)
    }

    // Add a project from the selector
    const handleAddProject = (project: Project) => {
        handleProjectToggle(project.id)
    }

    // Remove a feature from the selected features
    const handleRemoveFeature = (featureId: number) => {
        handleFeatureToggle(featureId)
    }

    const handleValueChange = (featureId: number, decimalValue?: number, freeformValue?: string, referenceValue?: number) => {
        // Find the feature in our list
        const featureIndex = composerCheckin.features.findIndex(f => f.feature_id === featureId)
        let updatedFeatures = [...composerCheckin.features]

        // Find the feature to determine its type
        const feature = recentFeatures.find(f => f.id === featureId) ||
                       features.find(f => f.id === featureId)

        const featureType = feature?.config?.type || 'decimal'

        if (featureIndex >= 0) {
            // Update existing feature
            updatedFeatures[featureIndex] = {
                ...updatedFeatures[featureIndex],
                decimal_value: storesDecimal(featureType) ? decimalValue : undefined,
                freeform_value: featureType === 'freeform' || featureType === 'enum' ? freeformValue : undefined,
                reference_value: featureType === 'reference' ? referenceValue : undefined,
                manual_override: ManualOverrideType.KEEP_VALUE
            }
        } else {
            // Add new feature with value
            updatedFeatures.push({
                feature_id: featureId,
                decimal_value: storesDecimal(featureType) ? decimalValue : undefined,
                freeform_value: featureType === 'freeform' || featureType === 'enum' ? freeformValue : undefined,
                reference_value: featureType === 'reference' ? referenceValue : undefined,
                manual_override: ManualOverrideType.KEEP_VALUE
            })
        }

        const updatedCheckin = {
            ...composerCheckin,
            features: updatedFeatures
        }

        onFieldEdited(editingCheckinId, "feature", updatedCheckin, featureId)
    }

    const handleListChange = (featureId: number, values: Array<{value: string | number}>) => {
        // Feature-level lock: any user mutation on a list takes the whole
        // feature out of AI control. We emit every current row with
        // KEEP_VALUE; if the user emptied the list, we drop a single
        // sentinel row (all value columns NULL, manual_override=DELETE) so
        // the parser still sees the lock on re-parse.
        const feature = recentFeatures.find(f => f.id === featureId) ||
                       features.find(f => f.id === featureId)
        if (!feature) return
        const listType = feature.config?.params?.type

        const normalizeValue = (v: string | number): string | number => {
            if (listType === 'num' || listType === 'bool') return typeof v === 'number' ? v : parseFloat(v as string)
            if (listType === 'reference') return typeof v === 'number' ? v : parseInt(v as string)
            return v.toString()
        }

        const buildNewRow = (val: string | number): CheckinFeature => {
            const r: CheckinFeature = { feature_id: featureId, manual_override: ManualOverrideType.KEEP_VALUE }
            if (listType === 'num' || listType === 'bool') r.decimal_value = val as number
            else if (listType === 'reference') r.reference_value = val as number
            else r.freeform_value = val as string
            return r
        }

        const otherFeatures = composerCheckin.features.filter(f => f.feature_id !== featureId)
        const resultRows: CheckinFeature[] = values.length > 0
            ? values.map(item => buildNewRow(normalizeValue(item.value)))
            : [{ feature_id: featureId, manual_override: ManualOverrideType.DELETE }]

        const updatedCheckin = {
            ...composerCheckin,
            features: [...otherFeatures, ...resultRows],
        }

        onFieldEdited(editingCheckinId, "feature", updatedCheckin, featureId)
    }

    // Wired into every list-typed `EnhancedFeatureToken`: tracks which
    // feature's list popover is currently open so the chip survives an
    // empty-list mid-edit (see `featuresForDisplay`). Guarded against
    // out-of-order false-after-true emissions from a different chip.
    const handleListEditStateChange = useCallback((featureId: number, editing: boolean) => {
        if (editing) {
            setEditingFeatureId(featureId)
        } else {
            setEditingFeatureId((curr) => (curr === featureId ? null : curr))
        }
    }, [])

    // Resolves the user-global Locations feature id, bootstrapping it on
    // first call and caching the full Feature locally so the chip lookup
    // map (which combines `recentFeatures + features + bootstrappedFeatures`)
    // can render the new token without a page refresh.
    const ensureLocationsFeature = useCallback(async (): Promise<Feature> => {
        const fromProps = [...recentFeatures, ...features].find(
            (f) => !f.project_id && f.title === 'Locations'
        )
        if (fromProps) return fromProps
        const fromCache = bootstrappedFeatures.find(
            (f) => !f.project_id && f.title === 'Locations'
        )
        if (fromCache) return fromCache
        const fetched = await getLocationsSystemFeature()
        setBootstrappedFeatures((prev) =>
            prev.some((f) => f.id === fetched.id) ? prev : [...prev, fetched]
        )
        return fetched
    }, [recentFeatures, features, bootstrappedFeatures])

    // Append a single Locations row to the checkin. Keeps every other row
    // intact (unlike `handleLocationListChange`, which is a full wipe-and-
    // replace driven by the list editor's final commit). Used by the inline
    // selector and the map picker entry.
    const appendLocationRow = useCallback(async (row: CheckinFeature) => {
        const feature = await ensureLocationsFeature()
        const newRow: CheckinFeature = {
            ...row,
            feature_id: feature.id,
            manual_override: ManualOverrideType.KEEP_VALUE,
        }
        const updatedCheckin = {
            ...composerCheckin,
            features: [...composerCheckin.features, newRow],
        }
        onFieldEdited(editingCheckinId, 'feature', updatedCheckin, feature.id)
    }, [composerCheckin, editingCheckinId, ensureLocationsFeature, onFieldEdited])

    const handleSelectPlace = useCallback(async (place: SelectedPlace) => {
        // Seed the page-level collection-items cache before the chip renders.
        // Without this, picking a private place that isn't already referenced
        // by some other checkin lands a row whose name lookup misses, and the
        // location chip appears nameless until the next fetchData.
        if (onCollectionItemReady && place.lat != null && place.lng != null) {
            onCollectionItemReady({
                id: place.collectionItemId,
                data: {
                    name: place.name,
                    lat: place.lat,
                    lng: place.lng,
                    address: place.address,
                    google_place_id: place.googlePlaceId,
                },
            })
        }
        await appendLocationRow({
            feature_id: 0, // overwritten in appendLocationRow
            reference_value: place.collectionItemId,
        })
    }, [appendLocationRow, onCollectionItemReady])

    const handlePickCoords = useCallback(async (coords: { latitude: number; longitude: number }) => {
        await appendLocationRow({
            feature_id: 0,
            latitude: coords.latitude,
            longitude: coords.longitude,
        })
    }, [appendLocationRow])

    // Mirror of `ensureLocationsFeature` for the People system feature.
    const ensurePeopleFeature = useCallback(async (): Promise<Feature> => {
        const fromProps = [...recentFeatures, ...features].find(
            (f) => !f.project_id && f.title === 'People'
        )
        if (fromProps) return fromProps
        const fromCache = bootstrappedFeatures.find(
            (f) => !f.project_id && f.title === 'People'
        )
        if (fromCache) return fromCache
        const fetched = await getPeopleSystemFeature()
        setBootstrappedFeatures((prev) =>
            prev.some((f) => f.id === fetched.id) ? prev : [...prev, fetched]
        )
        return fetched
    }, [recentFeatures, features, bootstrappedFeatures])

    const handleSelectPerson = useCallback(async (person: SelectedPerson) => {
        // Seed the page-level cache so the chip renders with the real name
        // immediately. Mirrors `handleSelectPlace`'s rationale: a freshly
        // inline-created Person isn't in the page's collection-items lookup
        // yet, and the chip would otherwise render as "Person #N" until the
        // next fetchData.
        if (onCollectionItemReady) {
            onCollectionItemReady({
                id: person.collectionItemId,
                data: {
                    'first name': person.firstName,
                    'last name': person.lastName ?? null,
                    nickname: person.nickname ?? null,
                    relation: person.relation ?? null,
                },
            })
        }
        const feature = await ensurePeopleFeature()
        const newRow: CheckinFeature = {
            feature_id: feature.id,
            reference_value: person.collectionItemId,
            manual_override: ManualOverrideType.KEEP_VALUE,
        }
        const updatedCheckin = {
            ...composerCheckin,
            features: [...composerCheckin.features, newRow],
        }
        onFieldEdited(editingCheckinId, 'feature', updatedCheckin, feature.id)
    }, [composerCheckin, editingCheckinId, ensurePeopleFeature, onCollectionItemReady, onFieldEdited])

    // Locations get their own list-change handler because each row carries
    // a richer payload (Places ref vs. coords-only). Same feature-level
    // lock semantics as handleListChange: every commit stamps KEEP_VALUE
    // on the current rows; an emptied list emits a sentinel row.
    const handleLocationListChange = (featureId: number, values: LocationRow[]) => {
        const buildNewRow = (row: LocationRow): CheckinFeature | null => {
            const r: CheckinFeature = { feature_id: featureId, manual_override: ManualOverrideType.KEEP_VALUE }
            if (row.referenceValue != null) {
                r.reference_value = row.referenceValue
            } else if (row.latitude != null && row.longitude != null) {
                r.latitude = row.latitude
                r.longitude = row.longitude
            } else {
                return null
            }
            return r
        }

        const otherFeatures = composerCheckin.features.filter(f => f.feature_id !== featureId)
        const builtRows = values
            .map(row => buildNewRow(row))
            .filter((r): r is CheckinFeature => r !== null)
        const resultRows: CheckinFeature[] = builtRows.length > 0
            ? builtRows
            : [{ feature_id: featureId, manual_override: ManualOverrideType.DELETE }]

        const updatedCheckin = {
            ...composerCheckin,
            features: [...otherFeatures, ...resultRows],
        }
        onFieldEdited(editingCheckinId, "feature", updatedCheckin, featureId)
    }

    // Map list-change handler. Each row carries both a key half and a
    // value half. Same feature-level lock semantics as handleListChange /
    // handleLocationListChange: every commit stamps KEEP_VALUE on the
    // current rows; an emptied map emits a sentinel row.
    const handleMapChange = (featureId: number, values: MapRow[]) => {
        const hasKey = (row: MapRow): boolean =>
            row.key_freeform_value != null ||
            row.key_decimal_value != null ||
            row.key_reference_value != null

        const buildNewRow = (row: MapRow): CheckinFeature | null => {
            if (!hasKey(row)) return null
            const r: CheckinFeature = {
                feature_id: featureId,
                manual_override: ManualOverrideType.KEEP_VALUE,
            }
            if (row.key_freeform_value != null) r.key_freeform_value = row.key_freeform_value
            if (row.key_decimal_value != null) r.key_decimal_value = row.key_decimal_value
            if (row.key_reference_value != null) r.key_reference_value = row.key_reference_value
            if (row.decimal_value != null) r.decimal_value = row.decimal_value
            if (row.freeform_value != null) r.freeform_value = row.freeform_value
            if (row.reference_value != null) r.reference_value = row.reference_value
            if (row.latitude != null) r.latitude = row.latitude
            if (row.longitude != null) r.longitude = row.longitude
            return r
        }

        const otherFeatures = composerCheckin.features.filter(f => f.feature_id !== featureId)
        // Keyless placeholder rows (seeded by handleFeatureToggle when the
        // user first picks the feature) are carried only while no real
        // pair exists yet — they aren't user data.
        const placeholderRows = composerCheckin.features.filter(
            f => f.feature_id === featureId &&
                 f.key_freeform_value == null &&
                 f.key_decimal_value == null &&
                 f.key_reference_value == null,
        )

        const builtRows = values
            .map(row => buildNewRow(row))
            .filter((r): r is CheckinFeature => r !== null)

        let resultRows: CheckinFeature[]
        let carryPlaceholders: CheckinFeature[] = []
        if (builtRows.length > 0) {
            resultRows = builtRows
        } else if (placeholderRows.length > 0) {
            // No real pairs yet — keep the placeholder, no sentinel.
            resultRows = []
            carryPlaceholders = placeholderRows
        } else {
            // User explicitly emptied a populated map → sentinel.
            resultRows = [{ feature_id: featureId, manual_override: ManualOverrideType.DELETE }]
        }

        const updatedCheckin = {
            ...composerCheckin,
            features: [...otherFeatures, ...carryPlaceholders, ...resultRows],
        }
        onFieldEdited(editingCheckinId, "feature", updatedCheckin, featureId)
    }

    // Drop a single DELETE tombstone from the composer state. The existing
    // PUT/parse pipeline does the rest: `_calculate_feature_changes` sees
    // "row was here, isn't anymore" and emits a real DELETE, then the
    // subsequent re-parse decides whether the AI re-attaches the value.
    const handleResetTombstone = useCallback((target: CheckinFeature) => {
        const targetKey = tombstoneKey(target)
        const updatedFeatures = composerCheckin.features.filter(
            (f) => !(f.manual_override === ManualOverrideType.DELETE && tombstoneKey(f) === targetKey)
        )
        const updatedCheckin = {
            ...composerCheckin,
            features: updatedFeatures,
        }
        onFieldEdited(editingCheckinId, 'feature', updatedCheckin, target.feature_id)
    }, [composerCheckin, editingCheckinId, onFieldEdited])

    // Explicit tab handlers
    const handleExplicitFeatureValueChange = (featureId: number, updates: Partial<CheckinFeature>) => {
        const newValues = new Map(explicitFeatureValues)
        const existing = newValues.get(featureId) || { feature_id: featureId, manual_override: ManualOverrideType.KEEP_VALUE }
        newValues.set(featureId, { ...existing, ...updates })
        setExplicitFeatureValues(newValues)
    }

    const handleTabChange = (tab: 'simple' | 'explicit') => {
        setActiveTab(tab)

        // When switching to explicit tab, sync current features to explicit values
        if (tab === 'explicit' && selectedProjectId) {
            const projectFeatureIds = projectFeatures.map(f => f.id)
            const relevantFeatures = composerCheckin.features.filter(cf =>
                projectFeatureIds.includes(cf.feature_id)
            )

            const valuesMap = new Map<number, CheckinFeature>()
            relevantFeatures.forEach(cf => {
                // Find the feature to determine its type
                const feature = projectFeatures.find(f => f.id === cf.feature_id)
                const featureType = feature?.config?.type || 'freeform'
                let displayValue = ''

                if (storesDecimal(featureType)) {
                    displayValue = cf.decimal_value?.toString() || ''
                } else if (featureType === 'reference') {
                    displayValue = cf.reference_value?.toString() || ''
                } else {
                    displayValue = cf.freeform_value || ''
                }

                valuesMap.set(cf.feature_id, {
                    ...cf,
                    freeform_value: displayValue // Store display value in freeform_value for the UI
                })
            })
            setExplicitFeatureValues(valuesMap)
        }

        // When switching back to simple tab, sync explicit values back to composerCheckin
        if (tab === 'simple' && activeTab === 'explicit') {
            const explicitFeatures = Array.from(explicitFeatureValues.values())
                .filter(cf => {
                    // Include features that have values OR are marked for deletion
                    return (cf.manual_override === ManualOverrideType.DELETE ||
                           cf.freeform_value !== undefined ||
                           cf.reference_value !== undefined)
                })
                .map(cf => {
                    // Parse the freeform_value into the correct type based on feature
                    const feature = projectFeatures.find(f => f.id === cf.feature_id)
                    const featureType = feature?.config?.type || 'freeform'

                    // If marked for deletion, keep it as is — except that a
                    // yes/no row may not carry the display string at all.
                    if (cf.manual_override === ManualOverrideType.DELETE) {
                        return featureType === 'bool' ? { ...cf, freeform_value: undefined } : cf
                    }

                    if (storesDecimal(featureType)) {
                        const numValue = parseFloat(cf.freeform_value || '')
                        return {
                            ...cf,
                            decimal_value: !isNaN(numValue) ? numValue : undefined,
                            freeform_value: undefined,
                            manual_override: cf.manual_override || ManualOverrideType.KEEP_VALUE
                        }
                    } else if (featureType === 'reference') {
                        const refValue = parseInt(cf.freeform_value || '')
                        return {
                            ...cf,
                            reference_value: !isNaN(refValue) ? refValue : undefined,
                            freeform_value: undefined,
                            manual_override: cf.manual_override || ManualOverrideType.KEEP_VALUE
                        }
                    }
                    return {
                        ...cf,
                        manual_override: cf.manual_override || ManualOverrideType.KEEP_VALUE
                    }
                })

            const otherFeatures = composerCheckin.features.filter(f =>
                !projectFeatures.some(pf => pf.id === f.feature_id)
            )

            const updatedCheckin = {
                ...composerCheckin,
                features: [...otherFeatures, ...explicitFeatures]
            }

            onFieldEdited(editingCheckinId, 'feature', updatedCheckin)
        }
    }

    return (
        <div className="bg-white p-4 rounded-lg shadow-md">
            {/* Tab Navigation with Action Buttons */}
            <div className="mb-3 border-b border-gray-200">
                <nav className="-mb-px flex justify-between items-center" aria-label="Tabs">
                    <div className="flex space-x-8">
                        <button
                            onClick={() => handleTabChange('simple')}
                            className={`${
                                activeTab === 'simple'
                                    ? 'border-amber-500 text-amber-800'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                        >
                            Simple
                        </button>
                        <button
                            onClick={() => handleTabChange('explicit')}
                            className={`${
                                activeTab === 'explicit'
                                    ? 'border-amber-500 text-amber-800'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                        >
                            Explicit
                        </button>
                    </div>

                    {/* Action buttons for existing checkins */}
                    {editingCheckinId !== null && (
                        <div className="flex space-x-1">
                            {onCancel && (
                                <motion.button
                                    whileHover={{ scale: 1.1, rotate: 90 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                    onClick={onCancel}
                                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                    title="Close"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </motion.button>
                            )}
                            {onDelete && (
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                    onClick={() => onDelete(editingCheckinId)}
                                    className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded"
                                    title="Delete"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </motion.button>
                            )}
                        </div>
                    )}
                </nav>
            </div>

            <div>
                {/* Wraps rather than overflowing: the date, its two steppers
                    and the time controls are more than a phone's width. */}
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        onClick={() => stepDate(-1)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-action"
                        title="Previous day"
                        aria-label="Previous day"
                    >
                        ‹
                    </motion.button>
                    <input
                        type="date"
                        value={composerCheckin.applies_to}
                        onChange={handleDateChange}
                        className="w-40 md:w-48 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                        required
                    />
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        onClick={() => stepDate(1)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-action"
                        title="Next day"
                        aria-label="Next day"
                    >
                        ›
                    </motion.button>

                    {/* Time of day. Optional, and empty on every checkin that
                        predates it: an empty input is `applies_at: null`, and
                        nothing renders it unless the user has turned the
                        timestamp display on (`docs/checkin_time.md`). The
                        control itself lives in `CheckinTimePicker`, which the
                        time-assignment dialog shows too — one way to express a
                        time, not two. */}
                    <CheckinTimePicker
                        appliesAt={composerCheckin.applies_at}
                        precision={composerCheckin.applies_precision}
                        onChange={handleTimeChange}
                        resetKey={editingCheckinId}
                    />
                </div>

{activeTab === 'simple' && (
                    <div className="mb-3">
                        <textarea
                            ref={bodyTextareaRef}
                            value={composerCheckin.body}
                            onChange={handleBodyChange}
                            onKeyDown={(e) => {
                                // Cmd/Ctrl+Enter parses now instead of waiting
                                // out the timer. Plain Enter stays a newline.
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onParseNow) {
                                    e.preventDefault()
                                    onParseNow()
                                }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                            rows={2}
                            placeholder="What happened today?"
                            required
                        />
                    </div>
                )}

                {/* Simple Tab Content */}
                {activeTab === 'simple' && (
                    <div className="mb-4">
                        {/* Unified Project/Feature Selector */}
                        <div className="mt-4 mb-4">
                            <UnifiedSelector
                                placeholder="Add project, feature, location, or person..."
                                selectedItems={[
                                    ...composerCheckin.projects
                                        .filter(p => p.manual_override !== ManualOverrideType.DELETE)
                                        .map(p => ({ type: 'project' as const, id: p.project_id })),
                                    ...composerCheckin.features
                                        .filter(f => f.manual_override !== ManualOverrideType.DELETE)
                                        .map(f => ({ type: 'feature' as const, id: f.feature_id })),
                                    // Already-attached private places — block re-picks
                                    // via the autocomplete. Restrict to rows on a
                                    // `list[location]` feature so unrelated reference
                                    // features don't accidentally dedupe a place that
                                    // happens to share their integer id.
                                    ...composerCheckin.features
                                        .filter(f => {
                                            if (f.manual_override === ManualOverrideType.DELETE) return false
                                            if (f.reference_value == null) return false
                                            const def = allFeaturesById.get(f.feature_id)
                                            return def?.config?.type === 'list' && def?.config?.params?.type === 'location'
                                        })
                                        .map(f => ({ type: 'place' as const, id: f.reference_value as number })),
                                    // Already-attached people — block re-picks via the
                                    // autocomplete. The People system feature is the only
                                    // `list[reference]` whose collection points at the
                                    // user's People collection, so a dedup heuristic
                                    // rooted on the feature title='People' is safer than
                                    // chasing the collection_id (which varies per user).
                                    ...composerCheckin.features
                                        .filter(f => {
                                            if (f.manual_override === ManualOverrideType.DELETE) return false
                                            if (f.reference_value == null) return false
                                            const def = allFeaturesById.get(f.feature_id)
                                            return !def?.project_id && def?.title === 'People'
                                        })
                                        .map(f => ({ type: 'person' as const, id: f.reference_value as number }))
                                ]}
                                onSelectProject={handleAddProject}
                                onSelectFeature={handleAddFeature}
                                onSelectPlace={handleSelectPlace}
                                onPickCoords={handlePickCoords}
                                onSelectPerson={handleSelectPerson}
                                onCollectionItemReady={onCollectionItemReady}
                            />
                        </div>

                    {/* Projects and Features Display */}
                    {(() => {
                        // Shared with the autocomplete's place-dedup filter via
                        // the component-level `allFeaturesById` memo.
                        const allFeatures = allFeaturesById;

                        // Track which features have been displayed with projects
                        const displayedFeatureIds = new Set<number>();

                        return (
                            <>
                                {/* Projects with their features */}
                                <div className="space-y-2 mb-4">
                                    {composerCheckin.projects
                                        .filter(p => p.manual_override !== ManualOverrideType.DELETE)
                                        .map(checkinProject => {
                                            // First try to find in projectDetails (immediate display), then in loaded projects
                                            const project = projectDetails?.find(p => p.id === checkinProject.project_id) ||
                                                          projects.find(p => p.id === checkinProject.project_id);
                                            if (!project) return null;

                                            // Check if the project has been manually overridden
                                            const isProjectManuallyOverridden = checkinProject.manual_override === ManualOverrideType.KEEP_VALUE;

                                            // Group features by feature_id to handle lists properly
                                            const featureGroups = new Map<number, CheckinFeature[]>();

                                            // Find all checkin features that belong to this project
                                            featuresForDisplay
                                                .filter(cf => cf.manual_override !== ManualOverrideType.DELETE)
                                                .forEach(cf => {
                                                    const feature = allFeatures.get(cf.feature_id);
                                                    if (feature && feature.project_id === project.id) {
                                                        if (!featureGroups.has(cf.feature_id)) {
                                                            featureGroups.set(cf.feature_id, []);
                                                        }
                                                        featureGroups.get(cf.feature_id)!.push(cf);
                                                        displayedFeatureIds.add(cf.feature_id);
                                                    }
                                                });

                                            return (
                                                <div key={project.id} className="flex flex-wrap items-center gap-2">
                                                    {/* Project token */}
                                                    <motion.div
                                                        initial={{ opacity: 0, scale: 0.6 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                        whileHover={{ y: -1 }}
                                                        whileTap={{ scale: 0.96 }}
                                                        style={{ transformOrigin: 'left center' }}
                                                        className="inline-flex items-center rounded-full text-xs font-medium bg-plum-tint text-plum overflow-hidden whitespace-nowrap"
                                                    >
                                                        <span className="px-3 py-1">
                                                            {project.emoji && <span className="mr-1">{project.emoji}</span>}
                                                            {project.title}
                                                        </span>
                                                        {isProjectManuallyOverridden && (
                                                            <>
                                                                <div className="h-5 mx-1 border-l border-plum/30"></div>
                                                                <div className="text-amber-800" title="Manually set">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                            </>
                                                        )}
                                                        <div className="h-5 mx-1 border-l border-plum/30"></div>
                                                        <button
                                                            onClick={() => handleProjectToggle(project.id)}
                                                            className="pr-2 py-1 text-amber-800 hover:text-amber-900"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </motion.div>

                                                    {/* Features for this project */}
                                                    {Array.from(featureGroups.entries()).map(([featureId, checkinFeatures]) => {
                                                        const feature = allFeatures.get(featureId);
                                                        if (!feature) return null;

                                                        return (
                                                            <EnhancedFeatureToken
                                                                key={feature.id}
                                                                feature={feature}
                                                                featureData={feature.config?.type === 'list' || feature.config?.type === 'map' ? checkinFeatures : checkinFeatures[0]}
                                                                collectionItems={collectionItems}
                                                                onRemove={handleRemoveFeature}
                                                                onValueChange={handleValueChange}
                                                                onListChange={handleListChange}
                                                                onLocationListChange={handleLocationListChange}
                                                                onMapChange={handleMapChange}
                                                                onCollectionItemReady={onCollectionItemReady}
                                                                onListEditStateChange={handleListEditStateChange}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                </div>

                                {/* Standalone Features (features without projects or not yet displayed) */}
                                {(() => {
                                    // Group features by feature_id to handle lists properly
                                    const standaloneFeatureGroups = new Map<number, CheckinFeature[]>();


                                    featuresForDisplay
                                        .filter(f => f.manual_override !== ManualOverrideType.DELETE)
                                        .forEach(cf => {
                                            // Skip if already displayed with a project
                                            if (displayedFeatureIds.has(cf.feature_id)) {
                                                return;
                                            }

                                            const feature = allFeatures.get(cf.feature_id);

                                            // Include if no feature found (shouldn't happen) or feature has no project
                                            if (!feature || !feature.project_id) {
                                                if (!standaloneFeatureGroups.has(cf.feature_id)) {
                                                    standaloneFeatureGroups.set(cf.feature_id, []);
                                                }
                                                standaloneFeatureGroups.get(cf.feature_id)!.push(cf);
                                            }
                                        });

                                    if (standaloneFeatureGroups.size === 0) return null;

                                    return (
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {Array.from(standaloneFeatureGroups.entries()).map(([featureId, checkinFeatures]) => {
                                                const feature = allFeatures.get(featureId);
                                                if (!feature) return null;

                                                return (
                                                    <EnhancedFeatureToken
                                                        key={feature.id}
                                                        feature={feature}
                                                        featureData={feature.config?.type === 'list' || feature.config?.type === 'map' ? checkinFeatures : checkinFeatures[0]}
                                                        collectionItems={collectionItems}
                                                        onRemove={handleRemoveFeature}
                                                        onValueChange={handleValueChange}
                                                        onListChange={handleListChange}
                                                        onLocationListChange={handleLocationListChange}
                                                        onMapChange={handleMapChange}
                                                        onCollectionItemReady={onCollectionItemReady}
                                                        onListEditStateChange={handleListEditStateChange}
                                                    />
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </>
                        );
                    })()}

                    {/* Manually-removed entries — single per-checkin disclosure. */}
                    {(() => {
                        // A valued DELETE row is a scalar's tombstone. A
                        // value-less one is a list/map sentinel — the whole
                        // list was cleared — and it belongs here too, as a
                        // single "cleared" entry rather than one line per
                        // removed value (the new model keeps no per-row
                        // history; `checkin_edit_events` does).
                        const tombstones = composerCheckin.features.filter(
                            (f) => f.manual_override === ManualOverrideType.DELETE
                        )
                        const projectTombstones = composerCheckin.projects.filter(
                            (p) => p.manual_override === ManualOverrideType.DELETE
                        )
                        if (tombstones.length === 0 && projectTombstones.length === 0) return null
                        return (
                            <TombstoneDisclosure
                                tombstones={tombstones}
                                projectTombstones={projectTombstones}
                                featuresById={allFeaturesById}
                                projectsById={projectsById}
                                collectionItems={collectionItems}
                                expanded={tombstonesExpanded}
                                onToggle={() => setTombstonesExpanded((v) => !v)}
                                onReset={handleResetTombstone}
                                onResetProject={handleProjectToggle}
                            />
                        )
                    })()}
                </div>
                )}

                {/* Saving State Indicator - After Simple Tab */}
                {activeTab === 'simple' && (
                    <SavingStateIndicator
                        state={savingState}
                        parseCountdown={parseCountdown}
                        onParseNow={onParseNow}
                        activity={activity}
                        onToggleParseExpanded={onToggleParseExpanded}
                    />
                )}

                {/* Explicit Tab Content */}
                {activeTab === 'explicit' && (
                    <div className="mb-4">
                        {/* Project Selector for Explicit Tab */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Project
                            </label>
                            <select
                                value={selectedProjectId || ''}
                                onChange={(e) => setSelectedProjectId(Number(e.target.value))}
                                className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                            >
                                {projects.map(project => (
                                    <option key={project.id} value={project.id}>
                                        {project.emoji && `${project.emoji} `}{project.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Features Grid */}
                        <div className="mb-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Features</h3>
                            {projectFeatures.length === 0 ? (
                                <p className="text-gray-500 text-sm">No features found for this project.</p>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {projectFeatures.map(feature => {
                                        const value = explicitFeatureValues.get(feature.id)
                                        const featureConfig = feature.config || {}
                                        const featureType = featureConfig.type || 'freeform'
                                        const params = featureConfig.params || {}

                                        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
                                            const inputValue = e.target.value

                                            if (inputValue === '') {
                                                // Empty input - mark as DELETE
                                                const updates = {
                                                    feature_id: feature.id,
                                                    manual_override: ManualOverrideType.DELETE,
                                                    // Clear the display value (a yes/no row may carry none)
                                                    freeform_value: featureType === 'bool' ? undefined : '',
                                                }

                                                handleExplicitFeatureValueChange(feature.id, updates)

                                                // Update the checkin immediately with DELETE override
                                                const updatedExplicitFeatures = Array.from(explicitFeatureValues.values())
                                                    .filter(cf => cf.feature_id !== feature.id)
                                                    .concat([updates])

                                                const otherFeatures = composerCheckin.features.filter(f =>
                                                    !projectFeatures.some(pf => pf.id === f.feature_id)
                                                )

                                                const updatedCheckin = {
                                                    ...composerCheckin,
                                                    features: [...otherFeatures, ...updatedExplicitFeatures]
                                                }

                                                onFieldEdited(editingCheckinId, 'feature', updatedCheckin, feature.id)
                                            } else {
                                                // Has value - add/update in explicit values
                                                let updates: Partial<CheckinFeature> = {}

                                                if (featureType === 'decimal' || featureType === 'rating' || featureType === 'labeled_rating') {
                                                    // Always store as freeform for display, let the final submission parse it
                                                    updates.freeform_value = inputValue
                                                } else if (featureType === 'reference') {
                                                    updates.freeform_value = inputValue
                                                } else {
                                                    updates.freeform_value = inputValue
                                                }

                                                handleExplicitFeatureValueChange(feature.id, updates)

                                                // Trigger the save handler with debouncing like simple mode
                                                const updatedExplicitFeatures = Array.from(explicitFeatureValues.values())
                                                    .filter(cf => cf.feature_id !== feature.id) // Remove current feature
                                                    .concat([{ feature_id: feature.id, manual_override: ManualOverrideType.KEEP_VALUE, ...updates }]) // Add updated feature

                                                const otherFeatures = composerCheckin.features.filter(f =>
                                                    !projectFeatures.some(pf => pf.id === f.feature_id)
                                                )

                                                // Parse explicit features to correct types for saving
                                                const parsedExplicitFeatures = updatedExplicitFeatures.map(cf => {
                                                    const feat = projectFeatures.find(f => f.id === cf.feature_id)
                                                    const featType = feat?.config?.type || 'freeform'

                                                    if (storesDecimal(featType)) {
                                                        const numValue = parseFloat(cf.freeform_value || '')
                                                        return {
                                                            ...cf,
                                                            decimal_value: !isNaN(numValue) ? numValue : undefined,
                                                            freeform_value: undefined
                                                        }
                                                    } else if (featType === 'reference') {
                                                        const refValue = parseInt(cf.freeform_value || '')
                                                        return {
                                                            ...cf,
                                                            reference_value: !isNaN(refValue) ? refValue : undefined,
                                                            freeform_value: undefined
                                                        }
                                                    }
                                                    return cf
                                                })

                                                const updatedCheckin = {
                                                    ...composerCheckin,
                                                    features: [...otherFeatures, ...parsedExplicitFeatures]
                                                }

                                                onFieldEdited(editingCheckinId, 'feature', updatedCheckin, feature.id)
                                            }
                                        }

                                        const getCurrentValue = () => {
                                            if (!value) return ''
                                            // If marked for deletion, show empty
                                            if (value.manual_override === ManualOverrideType.DELETE) return ''
                                            return value.freeform_value || ''
                                        }

                                        return (
                                            <div key={feature.id} className="border rounded-lg p-3">
                                                {/* Feature Name and Unit */}
                                                <div className="text-sm font-medium text-gray-700 mb-2">
                                                    {feature.title}
                                                    {feature.unit && <span className="text-gray-500"> ({feature.unit})</span>}
                                                </div>

                                                {/* Input Field */}
                                                <div>
                                                    {featureType === 'enum' ? (
                                                        <select
                                                            value={getCurrentValue()}
                                                            onChange={handleInputChange}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-action"
                                                        >
                                                            <option value="">-</option>
                                                            {params.values?.map((val: any) => (
                                                                <option key={val} value={val}>{val}</option>
                                                            ))}
                                                        </select>
                                                    ) : featureType === 'bool' ? (
                                                        <select
                                                            value={getCurrentValue()}
                                                            onChange={handleInputChange}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-action"
                                                        >
                                                            <option value="">-</option>
                                                            <option value="1">{boolLabels(params)[0]}</option>
                                                            <option value="0">{boolLabels(params)[1]}</option>
                                                        </select>
                                                    ) : featureType === 'rating' ? (
                                                        <select
                                                            value={getCurrentValue()}
                                                            onChange={handleInputChange}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-action"
                                                        >
                                                            <option value="">-</option>
                                                            {Array.from({ length: params.max || 5 }, (_, i) => i + 1).map(rating => (
                                                                <option key={rating} value={rating}>{rating}</option>
                                                            ))}
                                                        </select>
                                                    ) : featureType === 'labeled_rating' ? (
                                                        <select
                                                            value={getCurrentValue()}
                                                            onChange={handleInputChange}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-action"
                                                        >
                                                            <option value="">-</option>
                                                            {params.labels?.map((label: string, index: number) => (
                                                                <option key={index} value={index + 1}>{label}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={getCurrentValue()}
                                                            onChange={handleInputChange}
                                                            placeholder="-"
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-action"
                                                            inputMode={featureType === 'decimal' ? 'decimal' : 'text'}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Note Input at Bottom */}
                        <div className="mb-3">
                            <input
                                type="text"
                                value={composerCheckin.body}
                                onChange={handleBodyChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                                placeholder="What happened today?"
                            />
                        </div>
                    </div>
                )}

                {/* Saving State Indicator - After Explicit Tab */}
                {activeTab === 'explicit' && (
                    <SavingStateIndicator
                        state={savingState}
                        parseCountdown={parseCountdown}
                        onParseNow={onParseNow}
                        activity={activity}
                        onToggleParseExpanded={onToggleParseExpanded}
                    />
                )}

                </div>
            </div>
    )
}

export default CheckinComposer